import React from 'react';
import type { ColumnsType } from 'antd/es/table';
import { LoadingOutlined } from '@ant-design/icons';
import { PERMISSIONS } from 'constants/permissions';
import { HasAccess } from 'components/common/accessControl';
import { Button, Col, Input, PageTitle, Row, Table, Tooltip, Typography } from 'components';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import { DeleteIcon, FilterIcon, PlusIcon, ViewIcon } from 'components/common/icons';
import { AssignedUsersDrawer } from './assignedUsersDrawer';
import styles from './styles.module.scss';
import { DeleteModal } from './delete';
import { useRoles } from './useRoles';
import { Filters } from './filters';

const { Search } = Input;

export interface RoleDataType {
  id: string;
  uuid: string | number;
  name: string;
  createDate: any;
  lastModifiedDate: any;
}

export const Roles: React.FC = () => {
  const { hasAccess } = useAccessControl();
  const {
    filtersForm,
    onCloseFilters,
    onOpenFilters,
    onAddNew,
    openFilters,
    drawerState,
    handleTableChange,
    routeChange,
    isLoading,
    pagination,
    onSearch,
    data,
    assignedUsersData,
    total,
    applyFilters,
    resetFilters,
    onDeleteModalStateChange,
    deleteItem,
    onExpand,
    onRedirectToUser,
  } = useRoles();
  const columns: ColumnsType<RoleDataType> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (_, { uuid }) => {
        return uuid;
      },
    },
    {
      title: 'Role',
      key: 'name',
      dataIndex: 'name',
      sorter: true,
      width: 200, // Set max width
      ellipsis: true, // Enable ellipsis
      render: (value) => {
        return (
          <Tooltip title={value}>
            <div className={'text-ellipsis'}>{value}</div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Assigned Users',
      key: 'users',
      width: 258,
      render: ({ users }) => {
        return (
          <Row align={'middle'} wrap={false}>
            <Row className={styles.users_column} wrap={false}>
              {!!users?.length && (
                <Tooltip title={`${users[0]?.firstName} ${users[0]?.lastName}`}>
                  <Typography.Text
                    underline
                    type={'secondary'}
                    style={{ cursor: 'pointer', marginRight: 4 }}
                    onClick={() => {
                      onRedirectToUser(users[0].id);
                    }}
                  >
                    {users[0]?.firstName} {users[0]?.lastName}
                  </Typography.Text>
                </Tooltip>
              )}
            </Row>
            {users?.length > 1 && (
              <Button
                size={'small'}
                type={'text'}
                variant={'text'}
                style={{ fontSize: '14px', color: '#525866', backgroundColor: 'transparent', fontWeight: 400 }}
                onClick={() => onExpand(users)}
              >
                <Typography.Text underline type={'secondary'}>
                  +{users?.length - 1}
                </Typography.Text>
              </Button>
            )}
          </Row>
        );
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'formattedCreatedDate',
      key: 'createDate',
      minWidth: 120,
      render: (value) => value?.day,
      sorter: true,
    },
    {
      title: 'Last Updated Date',
      dataIndex: 'formattedLastModifiedDate',
      key: 'lastModifiedDate',
      minWidth: 120,
      render: (value) => value?.day,
      sorter: true,
    },
  ];

  if (hasAccess(PERMISSIONS.roles.role.read) || hasAccess(PERMISSIONS.roles.role.delete)) {
    columns.push({
      fixed: 'right',
      width: 68,
      key: 'actions',
      render: (value) => {
        return (
          <Row className={'gap-8'} wrap={false} align={'middle'} justify={'center'}>
            <HasAccess role={PERMISSIONS.roles.role.read}>
              <Button
                onClick={() => routeChange(value.id)}
                style={{ padding: 0, width: 16, height: 16 }}
                type={'text'}
                size={'small'}
                icon={<ViewIcon />}
              />
            </HasAccess>
            <HasAccess role={PERMISSIONS.roles.role.delete}>
              <Button
                onClick={() => onDeleteModalStateChange({ name: value.name, id: value.id })}
                style={{ padding: 0, width: 16, height: 16 }}
                type={'text'}
                size={'small'}
                icon={<DeleteIcon />}
              />
            </HasAccess>
          </Row>
        );
      },
    });
  }

  return (
    <Col span={24} className={styles.main}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Row align={'middle'} justify={'space-between'}>
            <PageTitle title={'Roles'} />
            <HasAccess role={PERMISSIONS.roles.role.write}>
              <Button type={'primary'} onClick={onAddNew} icon={<PlusIcon color={'#FFFFFF'} />}>
                Add New
              </Button>
            </HasAccess>
          </Row>
        </Col>
        <Col span={24} className={styles.main_container}>
          <Row>
            <Col span={24}>
              <Row justify={'end'} className={styles.actions_container}>
                <Col style={{ width: '300px' }}>
                  <Search enterButton={false} placeholder={'Search'} onChange={(e) => onSearch(e.target.value)} />
                </Col>
                <Button color="default" variant="filled" onClick={onOpenFilters} icon={<FilterIcon />} />
              </Row>
            </Col>
            <Col span={24} style={{ paddingTop: '20px' }}>
              <Table
                tableLayout={'auto'}
                bordered
                scroll={{
                  x: true,
                }}
                rowKey="id"
                size={'small'}
                dataSource={data}
                columns={columns}
                loading={{
                  indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                  spinning: isLoading,
                }}
                locale={
                  isLoading
                    ? { emptyText: null } // Hide "No Data" text while loading
                    : {} // Use default AntD "No Data" icon when empty
                }
                onChange={handleTableChange}
                pagination={{
                  position: ['bottomCenter'],
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} items`,
                  size: 'default',
                  current: pagination.page,
                  pageSize: pagination.size,
                  total: total,
                }}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <DeleteModal openModal={!!deleteItem} onDeleteModalClose={onDeleteModalStateChange} dataToDelete={deleteItem} />
      <Filters
        form={filtersForm}
        drawerOpen={openFilters}
        closeDrawer={onCloseFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
      />
      <AssignedUsersDrawer drawerState={drawerState} onExpand={onExpand} data={assignedUsersData} />
    </Col>
  );
};
